import React, { useMemo } from 'react';
import { NodeProps, NodeResizer } from 'reactflow';
import type { BoardTimelineData, BoardTimelineEvent } from '../../../types';
import { useBoardActions } from '../boardActions';
import NodeActionsToolbar from './NodeActionsToolbar';
import './TimelineNode.css';

function eventDays(e: BoardTimelineEvent): number {
  const d = Math.max(1, e.duration || 1);
  return e.unit === 'weeks' ? d * 7 : d;
}

function formatDuration(e: BoardTimelineEvent): string {
  const d = Math.max(1, e.duration || 1);
  return `${d} ${e.unit === 'weeks' ? (d === 1 ? 'week' : 'weeks') : d === 1 ? 'day' : 'days'}`;
}

const TimelineNode: React.FC<NodeProps<BoardTimelineData>> = ({ id, data, selected }) => {
  const { readOnly, editTimeline } = useBoardActions();
  const events = data.events || [];

  const segments = useMemo(() => {
    const total = events.reduce((sum, e) => sum + eventDays(e), 0) || 1;
    return events.map((e) => ({
      event: e,
      pct: (eventDays(e) / total) * 100,
    }));
  }, [events]);

  return (
    <>
      <NodeResizer
        isVisible={!!selected && !readOnly}
        minWidth={280}
        minHeight={120}
        lineClassName="board-resize-line"
        handleClassName="board-resize-handle"
      />
      <NodeActionsToolbar id={id} />
      <div
        className={`board-node board-timeline-node ${selected ? 'is-selected' : ''}`}
        onDoubleClick={() => {
          if (!readOnly) editTimeline(id);
        }}
      >
        {data.title && <div className="board-timeline-title">{data.title}</div>}
        {events.length === 0 ? (
          <div className="board-timeline-empty">No events — edit via Tools → Quick Timeline Builder</div>
        ) : (
          <>
            <div className="board-timeline-gantt" role="img" aria-label="Timeline chart">
              {segments.map(({ event, pct }, i) => (
                <div
                  key={event.id}
                  className="board-timeline-segment"
                  style={{
                    width: `${pct}%`,
                    background: `hsl(${(i * 47 + 210) % 360} 62% 52%)`,
                  }}
                  title={`${event.label}: ${formatDuration(event)}`}
                />
              ))}
            </div>
            <div className="board-timeline-labels">
              {segments.map(({ event, pct }) => (
                <div
                  key={event.id}
                  className="board-timeline-label-col"
                  style={{ width: `${pct}%` }}
                >
                  <span className="board-timeline-label">{event.label || 'Event'}</span>
                  <span className="board-timeline-duration">{formatDuration(event)}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default React.memo(TimelineNode);

export { eventDays, formatDuration };
